import {
  Affiliates,
  ClusterOperators,
  FirstPurchase,
  FirstPurchaseLogin,
  Installments,
  ItemPriceRange,
  ListPrice,
  ListPriceKeys,
  MarketingTags,
  OPERATORS,
  Subscription,
  SubscriptionCycleOperators,
  SubscriptionFrequency,
  SubscriptionType,
  UTMICampaign,
  ZIP_CODE_RANGE_OPERATOR,
  type Cluster,
  type Conditions,
  type Item,
  type OrderValue,
  type RnbCalculatorPayload,
  type ShippingLocation,
  type ShippingMethods,
  type UTM,
} from 'typings'

export function toAPIPaymentMethods(
  config?: Item[]
): Pick<Partial<RnbCalculatorPayload>, 'paymentsMethods'> {
  return { paymentsMethods: config?.length ? config : undefined }
}

export function toAPIBin(
  config?: string[]
): Pick<Partial<RnbCalculatorPayload>, 'restrictionsBins'> {
  return { restrictionsBins: config?.length ? config : undefined }
}

export function toAPIOrderValue(
  config?: OrderValue
): Pick<
  Partial<RnbCalculatorPayload>,
  'totalValueCeling' | 'totalValueFloor' | 'totalValueMode'
> {
  return {
    totalValueCeling: config?.max,
    totalValueFloor: config?.min,
    totalValueMode: config?.itemsToConsider,
  }
}

export function toAPIItemPriceRange(
  config?: ItemPriceRange
): Pick<Partial<RnbCalculatorPayload>, 'itemMinPrice' | 'itemMaxPrice'> {
  return {
    itemMaxPrice: config?.max,
    itemMinPrice: config?.min,
  }
}

export function toAPIUtm(
  config?: UTM
): Pick<Partial<RnbCalculatorPayload>, 'utmSource' | 'utmCampaign'> {
  return {
    utmSource: config?.utmSource.length
      ? config?.utmSource.join(',')
      : undefined,
    utmCampaign: config?.utmCampaign.length
      ? config?.utmCampaign.join(',')
      : undefined,
  }
}

export function toAPIUTMICampaign(
  config?: UTMICampaign
): Pick<Partial<RnbCalculatorPayload>, 'utmiCampaign'> {
  return {
    utmiCampaign: config?.utmiCampaign.length
      ? config?.utmiCampaign.join(',')
      : undefined,
  }
}

export function toAPICluster(
  config?: Cluster
): Pick<
  Partial<RnbCalculatorPayload>,
  'clusterExpressions' | 'clusterOperator'
> {
  return {
    clusterExpressions: config?.expressions,
    clusterOperator: config
      ? config.operator === ClusterOperators.ALL
        ? 'All'
        : 'Any'
      : undefined,
  }
}

export function toAPIShippingMethods(
  config: ShippingMethods | undefined
): Pick<RnbCalculatorPayload, 'slasIds' | 'isSlaSelected'> {
  return {
    slasIds: config?.map((item) => item.id) ?? [],
    isSlaSelected: !!config?.length,
  }
}

export function toAPIShippingLocation(
  config?: ShippingLocation
): Pick<Partial<RnbCalculatorPayload>, 'countries' | 'zipCodeRanges'> {
  return {
    countries: config?.shippingCountries.length
      ? config.shippingCountries
      : undefined,
    zipCodeRanges: config?.zipCodeRanges.length
      ? config.zipCodeRanges.map(({ from, to, operator }) => ({
          zipCodeFrom: from,
          zipCodeTo: to,
          inclusive: operator === ZIP_CODE_RANGE_OPERATOR.INCLUDE,
        }))
      : undefined,
  }
}

export function toAPIListPrice(
  config?: ListPrice
): Pick<
  Partial<RnbCalculatorPayload>,
  'compareListPriceAndPrice' | 'isDifferentListPriceAndPrice'
> {
  if (config === undefined) {
    return {
      compareListPriceAndPrice: false,
      isDifferentListPriceAndPrice: false,
    }
  }
  const isDefined = config === ListPriceKeys.DEFINED
  return {
    compareListPriceAndPrice: !isDefined,
    isDifferentListPriceAndPrice: isDefined,
  }
}

export function toAPIAffiliates(
  config: Affiliates | undefined
): Pick<RnbCalculatorPayload, 'affiliates' | 'affiliatesIsInclusive'> {
  if (config === undefined) {
    return {
      affiliates: [],
    }
  }
  const { affiliates, operator } = config
  return {
    affiliates,
    affiliatesIsInclusive: operator === OPERATORS.EQUALS,
  }
}

export function toAPIInstallments(
  config: Installments | undefined
): Partial<Pick<RnbCalculatorPayload, 'maxInstallment' | 'minInstallment'>> {
  if (config === undefined) {
    return {
      maxInstallment: undefined,
      minInstallment: undefined,
    }
  }
  const { max, min } = config
  return {
    ...(max ? { maxInstallment: max } : {}),
    ...(min ? { minInstallment: min } : {}),
  }
}

export function toApiHistoricalPurchaseValue(
  value: number | undefined
): Pick<Partial<RnbCalculatorPayload>, 'totalValuePurchase'> {
  return { totalValuePurchase: value }
}

export function toAPIMarketingTags(
  config: MarketingTags | undefined
): Pick<
  RnbCalculatorPayload,
  'marketingTags' | 'marketingTagsAreNotInclusive'
> {
  if (config === undefined)
    return {
      marketingTags: [],
      marketingTagsAreNotInclusive: false,
    }

  return {
    marketingTags: config.tags,
    marketingTagsAreNotInclusive: config.operator === OPERATORS.DIFFERENT,
  }
}

export function toAPIFirstPurchase(
  config: FirstPurchase | undefined
): Pick<RnbCalculatorPayload, 'isFirstBuy' | 'firstBuyIsProfileOptimistic'> {
  if (config === undefined) {
    return {
      isFirstBuy: false,
      firstBuyIsProfileOptimistic: false,
    }
  }

  return {
    isFirstBuy: true,
    firstBuyIsProfileOptimistic: config.login === FirstPurchaseLogin.OPTIONAL,
  }
}

export function toAPISubscriptionPlan(
  frequency: SubscriptionFrequency | undefined
) {
  if (!frequency) return undefined

  return {
    plan: {
      frequency: {
        periodicity: frequency.unit,
        interval: frequency.value,
      },
    },
  }
}

export function toAPISubscriptionOrders(config: Subscription) {
  const { type, filter } = config
  if (type === SubscriptionType.FIRST) return undefined

  const { cycle } = filter
  if (!cycle) return { recurrentOrders: {} }

  switch (cycle.operator) {
    case SubscriptionCycleOperators.EQUALS:
      return {
        recurrentOrders: { minCount: cycle.value, maxCount: cycle.value },
      }
    case SubscriptionCycleOperators.LESS_THAN:
      return {
        recurrentOrders: { maxCount: cycle.value },
      }
    case SubscriptionCycleOperators.GREATER_THAN:
      return {
        recurrentOrders: { minCount: cycle.value },
      }
    case SubscriptionCycleOperators.RANGE:
      return {
        recurrentOrders: { minCount: cycle.min, maxCount: cycle.max },
      }
  }
}

export function toAPISubscription(
  config: Subscription | undefined
): Pick<RnbCalculatorPayload, 'subscriptions'> {
  if (!config)
    return {
      subscriptions: undefined,
    }

  const { filter, type } = config

  return {
    subscriptions: {
      ...toAPISubscriptionPlan(filter.frequency),
      execution: {
        firstOrders: type !== SubscriptionType.RECURRING,
        ...toAPISubscriptionOrders(config),
      },
    },
  }
}

export function toAPIConditions(
  conditions: Conditions
): ReturnType<
  typeof toAPIPaymentMethods &
    typeof toAPIBin &
    typeof toAPIOrderValue &
    typeof toAPIUtm &
    typeof toAPICluster &
    typeof toAPIShippingMethods
> {
  return {
    ...toAPIPaymentMethods(conditions.paymentMethods),
    ...toAPIBin(conditions.bin),
    ...toAPIUtm(conditions.utm),
    ...toAPICluster(conditions.cluster),
    ...toAPIShippingMethods(conditions.shippingMethods),
    ...toAPIShippingLocation(conditions.shippingLocation),
  }
}
